import Seo from "@/components/Seo";
import { localBusinessSchema, breadcrumbSchema } from "@/data/site";
import { PageHero, Section, CtaBand } from "@/components/sections";
import { Reveal } from "@/components/motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const faqs = [
  {
    q: "How much does a website cost in Ghana?",
    a: "At Sankofa Launch Studio, websites start from GHS 2,200 for a one-page launch site and GHS 4,500 for a full multi-page business website. School and hotel websites start from GHS 6,000, online shops from GHS 9,500, and custom web apps from GHS 14,000. Prices are fixed and agreed before we start.",
  },
  {
    q: "How long does it take to build a website?",
    a: "We deliver most websites in four weeks using a clear, structured sprint. If you provide your content and feedback quickly, it can be even faster.",
  },
  {
    q: "Do you build websites for schools in Ghana?",
    a: "Yes. We build professional school websites with admissions enquiries, news, calendars and optional online fee payment via Mobile Money or Paystack. We also help schools meet Ghana's Data Protection Act (Act 843) requirements for children's data.",
  },
  {
    q: "Do you build hotel websites with online booking?",
    a: "Yes. We build mobile-first hotel websites that show rooms and rates, take direct booking requests by form and WhatsApp, and can accept deposits online — helping you reduce commission paid to online travel agencies.",
  },
  {
    q: "Can customers pay with Mobile Money (MoMo)?",
    a: "Yes. We build Mobile Money and card payments into shops, school fee pages and hotel deposits using trusted Ghanaian payment providers such as Paystack, Hubtel and Flutterwave.",
  },
  {
    q: "Do you host and maintain the website after launch?",
    a: "Yes. We offer prepaid managed hosting. As long as your hosting plan is active, we keep your website online, secure, backed up and maintained. Hosting starts from GHS 300 per month, with a saving for paying annually.",
  },
  {
    q: "How do payments work?",
    a: "You pay in stages as the project moves forward: 20% to book your sprint, 30% when scope is agreed, 30% when your draft is ready, and 20% before we go live. You never pay everything up front.",
  },
  {
    q: "Do I own my website and domain?",
    a: "You own your content, branding and domain. We manage the hosting and deployment while your hosting plan is active, and export or transfer rights are set out clearly in your contract.",
  },
  {
    q: "What areas of Ghana do you serve?",
    a: "We are based in Accra and work with schools, hotels and businesses across Ghana, mostly online and over WhatsApp, with in-person meetings where helpful.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Faq() {
  return (
    <>
      <Seo
        title="Frequently Asked Questions — Website Design in Ghana | Sankofa Launch Studio"
        description="Answers to common questions about website costs in Ghana, timelines, school and hotel websites, Mobile Money payments, hosting and how our payments work."
        jsonLd={[
          localBusinessSchema(),
          faqSchema,
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "FAQ", path: "/faq" }]),
        ]}
      />
      <PageHero
        eyebrow="FAQ"
        breadcrumb="Home / FAQ"
        title="Frequently asked questions"
        intro="Clear, honest answers about pricing, timelines, payments and how we work with schools, hotels and businesses across Ghana."
      />

      <Section className="bg-cream">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="overflow-hidden rounded-2xl border border-navy/10 bg-white px-6 shadow-soft"
                >
                  <AccordionTrigger className="py-5 text-left font-serif text-lg font-600 text-navy hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-base leading-relaxed text-ink">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </Section>

      <CtaBand title="Still have a question?" text="Message us on WhatsApp and we'll give you a straight answer." />
    </>
  );
}
